//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { ITikTokAudienceDailyMetrics } from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import {
  ITikTokAudienceGender,
  TikTokGender,
} from '@apis/tiktok-user/tiktokUserApiModel';
import { IDonutChartDataPoint } from '@components/charts/DonutChart';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { IInsightDualChartCustomOptions } from '@insights/components/common/trend-chart/InsightsDualChart';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import {
  AUDIENCE_EMPTY_DAILY_METRICS,
  BASE_CHART_HEIGHT,
  BASE_CHART_WIDTH,
  getGenderColor,
} from '@tiktok/components/analytics/audience/tiktokAudienceConstants';
import { ITikTokDailyAudienceMetric } from '@tiktok/redux/audience/tiktokAudienceModel';
import { isDefined } from '@utils/filterPredicates';
import { getDatesInRange } from '@utils/getDatesInRange';
import { normalizeToMap } from '@utils/normalize';

export const getInsightsDualChartCustomOptions =
  (): IInsightDualChartCustomOptions => {
    return {
      chartHeight: BASE_CHART_HEIGHT,
      chartWidth: BASE_CHART_WIDTH,
    };
  };

const getFormattedDate = (d: Date | string) => moment(d).format('YYYY-MM-DD');

export function fillInMissingAudienceDates(
  days: Record<string, ITikTokAudienceDailyMetrics>,
  start: DateString,
  end: DateString,
): ITikTokDailyAudienceMetric[] {
  // We want to use the timezone of the local machine to ensure that dates aren't shifted when the user is in a different timezone than the client's setting
  const timezone = TimeZoneId(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const startDate = DateString.toDate(start, timezone);
  const endDate = DateString.toDate(end, timezone);

  const dailyStatsByKey = Object.keys(days);
  const dailyStats = dailyStatsByKey.map((key) => {
    const dayMetrics = days[key];
    return {
      date: DateString.format(
        DateString.fromTimestamp(new Date(key), timezone),
        'YYYY-MM-DD',
      ),
      metrics: {
        changeInFollowers: dayMetrics?.changeInFollowers,
        followersCount: dayMetrics?.followersCount,
        profileViews: dayMetrics?.profileViews,
        videoViews: dayMetrics?.videoViews,
        likeCount: dayMetrics?.likeCount,
        commentCount: dayMetrics?.commentCount,
        shareCount: dayMetrics?.shareCount,
        audienceActivity: dayMetrics?.audienceActivity ?? [],
      },
    };
  });

  const { byId: existingDaysMap } = normalizeToMap(dailyStats, (day) =>
    getFormattedDate(DateString.fromString(day.date, 'YYYY-MM-DD')),
  );

  return getDatesInRange(startDate, endDate, { inclusive: true }).map(
    (date) => {
      const dateKey = getFormattedDate(date);
      const existingDay = existingDaysMap.get(dateKey);

      return (
        existingDay ?? {
          date: dateKey,
          metrics: AUDIENCE_EMPTY_DAILY_METRICS,
        }
      );
    },
  );
}

export const getDonutChartData = (
  audienceGenders: ITikTokAudienceGender[],
): IDonutChartDataPoint[] => {
  // If any of the returned items have an undefined gender tag, we don't want to render anything
  if (audienceGenders.some((item) => !isDefined(item.gender))) {
    return [];
  }

  // Next, we want to verify that the totals returned from TikTok add up to 100%
  const total = audienceGenders.reduce((accumulator, gender) => {
    return accumulator + (gender.percentage ?? 0);
  }, 0);

  // If they do not add up to 100%, and there isn't yet an 'Unspecified' category, we want to add one with an initial percentage of 0
  const updatedAudienceGenders =
    total < 1 &&
    !audienceGenders.some((item) => item.gender == TikTokGender.Other)
      ? [...audienceGenders, { gender: TikTokGender.Other, percentage: 0 }]
      : audienceGenders;

  // For each category, create an IDonutChartDataPoint
  return updatedAudienceGenders.map(({ gender, percentage }) => {
    const definedPercentage = percentage ?? 0;

    // If the totals did not add up to 100%, we want to add the difference to the 'Unspecified' category so the chart shows a full 100%
    const updatedPercentage =
      gender == TikTokGender.Other && total < 1
        ? definedPercentage + (1 - total)
        : definedPercentage;

    return {
      // gender should always be defined here from the above return, typescript just doesn't recognize it so we need the nullish coalescing
      name: gender ?? '',
      color: getGenderColor(gender ?? TikTokGender.Other),
      y: updatedPercentage * 100,
    };
  });
};

function getIsTestDateAfterTargetDate(
  testDate: DateString,
  targetDate: DateString,
): boolean {
  return DateString.compare(testDate, targetDate) >= 0;
}

export function getOptionalDateRangeAliases(
  earliestDate: DateString,
  timeZoneId: TimeZoneId,
): IAliasedDateRange[] {
  const nowDateString = DateString.fromTimestamp(new Date(), timeZoneId);

  const sevenDaysAgoDateString = DateString.addDays(nowDateString, -7);
  const thirtyDaysAgoDateString = DateString.addDays(nowDateString, -30);
  const threeMonthsAgoDateString = DateString.addMonths(nowDateString, -3);
  const oneYearAgoDateString = DateString.addYears(nowDateString, -1);

  return [
    ...(getIsTestDateAfterTargetDate(sevenDaysAgoDateString, earliestDate)
      ? [getAliasedDateRange('last7Days', timeZoneId)]
      : []),
    ...(getIsTestDateAfterTargetDate(thirtyDaysAgoDateString, earliestDate)
      ? [getAliasedDateRange('last30Days', timeZoneId)]
      : []),
    ...(getIsTestDateAfterTargetDate(threeMonthsAgoDateString, earliestDate)
      ? [getAliasedDateRange('last3Months', timeZoneId)]
      : []),
    ...(getIsTestDateAfterTargetDate(oneYearAgoDateString, earliestDate)
      ? [getAliasedDateRange('last1Year', timeZoneId)]
      : []),
  ];
}
